/**
 * User Permissions Context Provider
 *
 * Provides user permissions and license information throughout the app.
 * Uses TanStack Query for automatic caching and state management.
 *
 * Responsibilities:
 * - Fetch and cache user license information
 * - Compute derived permissions (canCreateRooms, canScheduleMeetings, etc.)
 * - Provide permission state to child components
 *
 * This context focuses on AUTHORIZATION (what user can do),
 * separate from UserContext which handles AUTHENTICATION (who user is).
 *
 * @example
 * ```tsx
 * const { canCreateRooms, hasValidLicense, isLoading } = useUserPermissions();
 *
 * if (canCreateRooms) {
 *   // Show create room button
 * }
 * ```
 */

import { useQueryClient } from '@tanstack/react-query';
import type React from 'react';
import { createContext, useCallback, useMemo } from 'react';
import DEFAULT_CONFIG from './helpers/config';
import {
  USER_PERMISSIONS_QUERY_KEY,
  useUserPermissionsQuery,
} from './internal_hooks/useUserPermissionsQuery';
import type { UserPermissionsContextValue, UserPermissionsProviderProps } from './types';

export const UserPermissionsContext = createContext<UserPermissionsContextValue | null>(null);

export const UserPermissionsProvider: React.FC<UserPermissionsProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();
  const options = useMemo(() => ({ ...DEFAULT_CONFIG }), []);
  const { data, isLoading, isSuccess, error, refetch } = useUserPermissionsQuery(options);

  const invalidatePermissions = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: USER_PERMISSIONS_QUERY_KEY });
  }, [queryClient]);

  const contextValue: UserPermissionsContextValue = useMemo(() => {
    const defaultPermissions = {
      license: null,
      canCreateRooms: false,
      canScheduleMeetings: false,
      hasValidLicense: false,
    };

    return {
      userPermissions: {
        ...defaultPermissions,
        ...data,
      },
      isLoading,
      isSuccess,
      error,
      refreshPermissions: refetch,
      invalidatePermissions,
    };
  }, [data, isLoading, isSuccess, error, refetch, invalidatePermissions]);

  return (
    <UserPermissionsContext.Provider value={contextValue}>
      {children}
    </UserPermissionsContext.Provider>
  );
};
